import React, { useState } from 'react';
import { Tag, X, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AppliedDiscount {
  code: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: number;
  description?: string;
}

interface DiscountCodeInputProps {
  cartTotal: number;
  onApply: (discount: AppliedDiscount | null) => void;
  appliedDiscount: AppliedDiscount | null;
}

export const DiscountCodeInput: React.FC<DiscountCodeInputProps> = ({
  cartTotal,
  onApply,
  appliedDiscount
}) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const validateAndApplyCode = async () => {
    if (!code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setIsValidating(true);
    
    try {
      const normalizedCode = code.trim().toUpperCase();
      
      // Query for valid discount code
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('code', normalizedCode)
        .eq('active', true)
        .single();

      if (error || !data) {
        toast.error('Invalid discount code');
        setIsValidating(false);
        return;
      }

      // Validate code is within date range
      const now = new Date();
      const startsAt = new Date(data.starts_at);
      const expiresAt = data.expires_at ? new Date(data.expires_at) : null;

      if (now < startsAt) {
        toast.error('This discount code is not yet active');
        setIsValidating(false);
        return;
      }

      if (expiresAt && now > expiresAt) {
        toast.error('This discount code has expired');
        setIsValidating(false);
        return;
      }

      // Check max uses
      if (data.max_uses && data.current_uses && data.current_uses >= data.max_uses) {
        toast.error('This discount code has reached its usage limit');
        setIsValidating(false);
        return;
      }

      // Check minimum purchase amount
      if (data.min_purchase_amount && cartTotal < data.min_purchase_amount) {
        toast.error(`Minimum purchase of £${data.min_purchase_amount.toFixed(2)} required for this code`);
        setIsValidating(false);
        return;
      }

      // Apply the discount
      const appliedDiscount: AppliedDiscount = {
        code: data.code,
        discountType: data.discount_type as 'percentage' | 'fixed_amount',
        discountValue: data.discount_value,
        description: data.description || undefined
      };

      onApply(appliedDiscount);
      toast.success(`Discount applied: ${data.description || data.code}`);
      setCode('');
      
    } catch (err) {
      console.error('Error validating discount code:', err);
      toast.error('Failed to validate discount code');
    } finally {
      setIsValidating(false);
    }
  };

  const removeDiscount = () => {
    onApply(null);
    toast.info('Discount removed');
  };

  const calculateSavings = () => {
    if (!appliedDiscount) return 0;
    
    if (appliedDiscount.discountType === 'percentage') {
      return (cartTotal * appliedDiscount.discountValue) / 100;
    }
    return Math.min(appliedDiscount.discountValue, cartTotal);
  };

  if (appliedDiscount) {
    const savings = calculateSavings();
    return (
      <div className="bg-solr-aurora/10 border border-solr-aurora/30 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-solr-aurora" />
            <div>
              <span className="font-medium text-solr-aurora">{appliedDiscount.code}</span>
              <p className="text-sm text-solr-starfield-silver">
                {appliedDiscount.discountType === 'percentage' 
                  ? `${appliedDiscount.discountValue}% off` 
                  : `£${appliedDiscount.discountValue.toFixed(2)} off`}
                {appliedDiscount.description && ` - ${appliedDiscount.description}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-solr-aurora font-medium">-£{savings.toFixed(2)}</span>
            <button
              onClick={removeDiscount}
              className="text-solr-starfield-silver hover:text-red-400 transition-colors"
              aria-label="Remove discount"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-solr-starfield-silver" />
          <Input
            type="text"
            placeholder="Enter discount code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && validateAndApplyCode()}
            className="pl-10 bg-solr-space-dark/50 border-solr-nebula-purple/20 uppercase"
            disabled={isValidating}
          />
        </div>
        <Button
          onClick={validateAndApplyCode}
          disabled={isValidating || !code.trim()}
          variant="outline"
          className="border-solr-nebula-purple/30 hover:bg-solr-nebula-purple/10"
        >
          {isValidating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </Button>
      </div>
    </div>
  );
};
